import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import { ArrowRight, ArrowUpRight, Plus, Minus } from "lucide-react";

export const Home = () => {
  const [stats, setStats] = useState({
    active_briefs: 142,
    active_properties: 88,
    total_matches: 310,
    total_moves: 64,
    avg_match_time_hours: 4.2,
  });
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get("/stats");
        setStats(res.data);
      } catch (e) { /* fallback */ }
    };
    fetchStats();
  }, []);

  const featured = [
    {
      area: "Notting Hill · W11",
      title: "Two-bedroom Garden Flat",
      price: "£3,200",
      period: "PCM",
      details: "2 Beds · 1 Bath · Communal garden",
      available: "Available June 2026",
      img: "https://images.unsplash.com/photo-1740596261028-29a7ad308157?fm=jpg&q=85&w=1400&auto=format&fit=crop",
    },
    {
      area: "Islington · N1",
      title: "Georgian Townhouse Apartment",
      price: "£2,850",
      period: "PCM",
      details: "2 Beds · 1 Bath · Private patio",
      available: "Available July 2026",
      img: "https://images.unsplash.com/photo-1764416166529-99edb0e405a6?fm=jpg&q=85&w=1400&auto=format&fit=crop",
    },
    {
      area: "Clapham · SW4",
      title: "Modern Riverside Studio",
      price: "£1,750",
      period: "PCM",
      details: "Studio · 1 Bath · Concierge building",
      available: "Available May 2026",
      img: "https://images.unsplash.com/photo-1784652810886-3bc71c1c1f46?fm=jpg&q=85&w=1400&auto=format&fit=crop",
    },
  ];

  const faqs = [
    { q: "How does FIND LET differ from Rightmove or Zoopla?", a: "FIND LET is demand-driven. Renters submit a single brief and our concierge desk pairs them privately with vetted inventory. There is no public property listing scroll." },
    { q: "Is FIND LET free for tenants?", a: "Submitting a brief and receiving matches is entirely free for renters. We are compensated by landlords and letting partners at the point of a successful placement." },
    { q: "How quickly do briefs receive matches?", a: `Our concierge desk currently returns first curated matches within an average of ${stats.avg_match_time_hours} hours during business days.` },
    { q: "Do you cover the whole of London?", a: "Yes. Our operating remit spans central, inner and outer London — from Mayfair and Kensington to Islington, Clapham, Hackney and beyond." },
  ];

  return (
    <div className="bg-[#F9FBFF] text-[#091F5C]">

      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          <div className="lg:col-span-6 space-y-8 fade-up">
            <span className="eyebrow inline-block">London Property · Concierge Service</span>

            <h1 className="display-hero text-[42px] sm:text-6xl lg:text-[68px] text-[#091F5C]">
              A more considered way to rent in London.
            </h1>

            <p className="text-base sm:text-lg text-[#4A5B78] font-light leading-relaxed max-w-xl">
              Tell us the home you are looking for. Our London concierge desk quietly pairs your brief with private landlord inventory, vetted agency stock and off-market residences — then coordinates viewings on your terms.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Link to="/find" data-testid="hero-cta-find" className="btn-primary inline-flex items-center justify-center space-x-2 group">
                <span>Submit a Search Brief</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/let" data-testid="hero-cta-let" className="btn-secondary inline-flex items-center justify-center space-x-2">
                <span>List a Property</span>
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-6 pt-10 border-t border-[#E5EBF5] max-w-xl">
              <div>
                <div className="text-3xl font-heading font-bold text-[#091F5C]">{stats.active_briefs}+</div>
                <div className="text-[11px] uppercase tracking-widest text-[#4A5B78] mt-1 font-medium">Active Briefs</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-bold text-[#091F5C]">{stats.avg_match_time_hours}h</div>
                <div className="text-[11px] uppercase tracking-widest text-[#4A5B78] mt-1 font-medium">Avg Match Time</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-bold text-[#091F5C]">100%</div>
                <div className="text-[11px] uppercase tracking-widest text-[#4A5B78] mt-1 font-medium">Verified Inventory</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 fade-up-delay-1">
            <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1544561221-d360f0a79df5?fm=jpg&q=85&w=1600&auto=format&fit=crop"
                alt="Row of London townhouses"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white px-6 py-5 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#334DAF] font-semibold">Central London</div>
                  <div className="text-[15px] font-heading font-semibold text-[#091F5C] mt-1">Prime Residences · Verified Inventory</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#4A5B78]">From</div>
                  <div className="text-[15px] font-heading font-semibold text-[#091F5C]">£1,750 PCM</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F9FBFF] py-24 lg:py-28 border-t border-[#E5EBF5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start mb-16">
            <div className="lg:col-span-5">
              <span className="eyebrow">Our Method</span>
              <h2 className="text-4xl lg:text-5xl font-heading font-bold text-[#091F5C] mt-4 leading-[1.1]">
                Three steps to the right London home.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <p className="text-[#4A5B78] font-light leading-relaxed text-base">
                Traditional portals force renters to compete against thousands of others for a decaying pool of stale listings. FIND LET reverses the flow — qualified demand is presented to landlords, and matches are delivered privately.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5EBF5] border border-[#E5EBF5]">
            {[
              { n: "01", h: "Define your brief", p: "Share target boroughs, commute anchors, budget, move-in window, household composition, and non-negotiables — pets, outside space, furnishing." },
              { n: "02", h: "Concierge pairing", p: "We match your brief against private landlord submissions, vetted agency feeds, and off-market inventory across Greater London." },
              { n: "03", h: "Viewings & move", p: "Approve matches, request private viewings in one click, and — once agreed — coordinate removals, cleaning, and utilities through FIND LET Move." },
            ].map((step) => (
              <div key={step.n} className="bg-white p-10 lg:p-12">
                <div className="text-[11px] tracking-[0.22em] text-[#334DAF] font-heading font-semibold">{step.n}</div>
                <h3 className="text-xl font-heading font-semibold text-[#091F5C] mt-4">{step.h}</h3>
                <p className="text-sm text-[#4A5B78] font-light leading-relaxed mt-3">{step.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL BAND */}
      <section className="bg-white py-24 lg:py-28 border-t border-[#E5EBF5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 relative aspect-[5/4] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1781370163418-e02a602826c2?fm=jpg&q=85&w=1400&auto=format&fit=crop"
              alt="London mews street"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="lg:col-span-6 space-y-6">
            <span className="eyebrow">Brief · Match · Move</span>
            <h2 className="text-3xl lg:text-[40px] font-heading font-bold text-[#091F5C] leading-tight">
              A quieter, more editorial approach to London rental.
            </h2>
            <p className="text-[#4A5B78] font-light leading-relaxed">
              We work slowly and deliberately. Every brief is read by a person. Every match is considered against your commute, lifestyle, and long-term intent. No spam, no chase calls, no pressure.
            </p>
            <div className="pt-2">
              <Link to="/about" className="editorial-link inline-flex items-center space-x-1 text-sm">
                <span>Read our manifesto</span>
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED RESIDENCES */}
      <section className="bg-[#F9FBFF] py-24 lg:py-28 border-t border-[#E5EBF5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <span className="eyebrow">Recent inventory</span>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-[#091F5C] mt-3">
                Homes recently matched by our desk
              </h2>
            </div>
            <Link to="/find" className="editorial-link inline-flex items-center space-x-1 text-sm">
              <span>Submit a brief to see private matches</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featured.map((p, i) => (
              <div key={i} data-testid={`featured-property-${i}`} className="group">
                <div className="relative aspect-[4/5] overflow-hidden bg-[#E8F2FE]">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                </div>
                <div className="mt-5">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#334DAF] font-semibold">{p.area}</div>
                  <h3 className="text-lg font-heading font-semibold text-[#091F5C] mt-2">{p.title}</h3>
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <span className="text-[#4A5B78] font-light">{p.details}</span>
                    <span className="font-heading font-semibold text-[#091F5C]">{p.price} <span className="text-xs font-light text-[#4A5B78]">{p.period}</span></span>
                  </div>
                  <div className="mt-3 text-xs text-[#4A5B78] font-light">{p.available}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE TIERS */}
      <section className="bg-white py-24 lg:py-28 border-t border-[#E5EBF5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <span className="eyebrow">Services</span>
            <h2 className="text-3xl lg:text-[40px] font-heading font-bold text-[#091F5C] mt-3">
              One brand. Four practices.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5EBF5] border border-[#E5EBF5]">
            {[
              { tag: "FIND", h: "For renters", p: "Curated concierge search for tenants who value time and discretion.", to: "/find", cta: "Submit a brief" },
              { tag: "LET", h: "For landlords", p: "Match your property with pre-qualified tenant briefs without the listing noise.", to: "/let", cta: "List a property" },
              { tag: "MOVE", h: "Relocation services", p: "Vetted removals, deep clean, storage, and utility onboarding across London.", to: "/move", cta: "Book a move" },
              { tag: "PARTNERS", h: "Agencies & portfolios", p: "Submit inventory via feed, spreadsheet, or link list for concierge distribution.", to: "/partners", cta: "Partner with us" },
            ].map((s) => (
              <div key={s.tag} className="bg-white p-8 lg:p-10 flex flex-col justify-between min-h-[280px]">
                <div>
                  <div className="text-[10px] tracking-[0.28em] font-heading font-bold text-[#334DAF]">{s.tag}</div>
                  <h3 className="text-lg font-heading font-semibold text-[#091F5C] mt-3">{s.h}</h3>
                  <p className="text-sm text-[#4A5B78] font-light leading-relaxed mt-3">{s.p}</p>
                </div>
                <Link to={s.to} className="mt-6 editorial-link text-sm inline-flex items-center space-x-1">
                  <span>{s.cta}</span>
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL PAIR */}
      <section className="bg-[#E8F2FE] py-24 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-12">
          <blockquote className="space-y-5">
            <div className="text-[#091F5C] font-heading text-4xl leading-none">"</div>
            <p className="text-lg text-[#091F5C] font-light leading-relaxed">
              We submitted our brief for a pet-friendly flat in Kensington on a Tuesday morning. By Wednesday afternoon, the desk had arranged a private viewing for a home that never touched the public portals.
            </p>
            <footer className="pt-3 border-t border-[#D0E4FE]">
              <div className="text-sm font-heading font-semibold text-[#091F5C]">Eleanor Vane</div>
              <div className="text-xs text-[#4A5B78] mt-1 font-light">Renter · Kensington W8</div>
            </footer>
          </blockquote>
          <blockquote className="space-y-5">
            <div className="text-[#091F5C] font-heading text-4xl leading-none">"</div>
            <p className="text-lg text-[#091F5C] font-light leading-relaxed">
              We no longer filter through fifty generic enquiries. FIND LET only pairs our properties with pre-screened tenants whose budgets and move dates align. Our void periods have effectively disappeared.
            </p>
            <footer className="pt-3 border-t border-[#D0E4FE]">
              <div className="text-sm font-heading font-semibold text-[#091F5C]">Richard Sterling</div>
              <div className="text-xs text-[#4A5B78] mt-1 font-light">Landlord · Sterling Mayfair Estates</div>
            </footer>
          </blockquote>
        </div>
      </section>

      {/* FAQ ACCORDION */}
      <section className="bg-[#F9FBFF] py-24 lg:py-28 border-t border-[#E5EBF5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <span className="eyebrow">Frequently asked</span>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-[#091F5C] mt-3">
              Answers, briefly.
            </h2>
          </div>
          <div className="border-t border-[#E5EBF5]">
            {faqs.map((f, i) => (
              <div key={i} className="border-b border-[#E5EBF5]">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`home-faq-toggle-${i}`}
                  className="w-full py-6 flex items-center justify-between text-left"
                >
                  <span className="text-base lg:text-lg font-heading font-semibold text-[#091F5C] pr-6">{f.q}</span>
                  {openFaq === i ? <Minus className="w-4 h-4 text-[#334DAF] flex-shrink-0" /> : <Plus className="w-4 h-4 text-[#334DAF] flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="pb-6 text-sm text-[#4A5B78] font-light leading-relaxed max-w-3xl">{f.a}</div>
                )}
              </div>
            ))}
          </div>
          <div className="pt-8">
            <Link to="/faqs" className="editorial-link inline-flex items-center space-x-1 text-sm">
              <span>See all questions</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#091F5C] text-white py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center space-y-8">
          <h2 className="text-3xl lg:text-[44px] font-heading font-bold leading-tight">
            A quieter route to your next London home.
          </h2>
          <p className="text-[#D0E4FE] font-light max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are searching for a home or letting one, our concierge desk is standing by to receive your brief.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <Link to="/find" data-testid="bottom-cta-find" className="px-8 py-3.5 bg-white text-[#091F5C] font-semibold text-sm tracking-wide hover:bg-[#E8F2FE] transition-colors">
              Submit a Search Brief
            </Link>
            <Link to="/let" data-testid="bottom-cta-let" className="px-8 py-3.5 border border-white text-white font-semibold text-sm tracking-wide hover:bg-white hover:text-[#091F5C] transition-colors">
              List Your Property
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
